import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, TOKEN_KEY } from "../lib/api";
import { Bell, MessagesSquare, CheckCheck, AtSign, Flag, Flame } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

const TOASTED_KEY = "amino_toasted_mention_ids";
const readToasted = () => {
  try { return new Set(JSON.parse(localStorage.getItem(TOASTED_KEY) || "[]")); }
  catch { return new Set(); }
};
const writeToasted = (set) => {
  try {
    const arr = Array.from(set).slice(-100);
    localStorage.setItem(TOASTED_KEY, JSON.stringify(arr));
  } catch { /* ignore */ }
};

export default function NotificationBell({ brand = "#0D9488" }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const toastedRef = useRef(readToasted());
  const esRef = useRef(null);
  const pollTimerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      const nextItems = data.items || [];
      setItems(nextItems);
      setUnread(data.unread || 0);
      // Prime toasted-set with current mentions so we don't re-fire on refresh
      nextItems
        .filter((n) => n.type === "community_mention")
        .forEach((n) => toastedRef.current.add(n.id));
      writeToasted(toastedRef.current);
    } catch { /* ignore polling errors */ }
  };

  // Handle a real-time notification arriving via SSE (or polling fallback)
  const handleIncoming = (n) => {
    setItems((cur) => (cur.some((x) => x.id === n.id) ? cur : [n, ...cur].slice(0, 50)));
    if (!n.read) setUnread((u) => u + 1);
    if (n.type === "community_mention" && !toastedRef.current.has(n.id)) {
      toast(`${n.actor_name} mentioned you`, {
        description: n.topic_title ? `in "${n.topic_title}"` : undefined,
        action: n.topic_id ? {
          label: "View",
          onClick: () => navigate(`/community/${n.topic_id}`),
        } : undefined,
        icon: <AtSign className="w-4 h-4 text-[#0D9488]" />,
      });
      toastedRef.current.add(n.id);
      writeToasted(toastedRef.current);
    }
  };

  const startPollingFallback = () => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setInterval(load, 30_000);
  };

  const startSSE = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token || typeof window === "undefined" || !window.EventSource) {
      startPollingFallback();
      return;
    }
    try {
      const es = new EventSource(`${API}/notifications/stream?token=${encodeURIComponent(token)}`);
      esRef.current = es;
      es.addEventListener("notification", (evt) => {
        try { handleIncoming(JSON.parse(evt.data)); } catch { /* ignore */ }
      });
      es.addEventListener("hello", () => { /* connected */ });
      es.onerror = () => {
        // EventSource auto-reconnects; if it repeatedly fails, fall back to poll.
        // We only spin up polling once — if SSE comes back, polling coexists (cheap).
        startPollingFallback();
      };
    } catch {
      startPollingFallback();
    }
  };

  useEffect(() => {
    load();
    startSSE();
    return () => {
      if (esRef.current) esRef.current.close();
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openPanel = async () => {
    setOpen((v) => !v);
    if (!open) await load();
  };

  const routeFor = (n) => {
    // Reports → jump to the admin moderation card, with the specific target open in the URL hash
    if (n.type === "report") return `/admin/settings#report-${n.report_id || ""}`;
    // Reactions on your shots → your history (where the shot lives)
    if (n.type === "reaction") return "/history";
    // Anything with a topic — mentions, replies, replies-to-comment, etc.
    if (n.topic_id) {
      const anchor = n.reply_id ? `#reply-${n.reply_id}` : "";
      return `/community/${n.topic_id}${anchor}`;
    }
    return null;
  };

  const handleItemClick = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    setOpen(false);
    const path = routeFor(n);
    if (path) navigate(path);
  };

  const handleMarkAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
    setItems((cur) => cur.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={openPanel}
        aria-label="Notifications"
        className="w-9 h-9 border border-slate-200 hover:border-slate-300 bg-slate-50 hover:bg-slate-50 flex items-center justify-center relative transition-colors"
        data-testid="notifications-bell"
      >
        <Bell className="w-4 h-4 text-slate-800" strokeWidth={2} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-mono-num font-bold"
            style={{ background: brand, color: "#0a0a0b" }}
            data-testid="notifications-unread-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-h-[520px] flex flex-col bg-white border border-slate-200 shadow-2xl z-50"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Community</div>
              <div className="font-display font-black uppercase tracking-tight text-sm text-slate-900">Notifications</div>
            </div>
            {items.some((i) => !i.read) && (
              <button
                onClick={handleMarkAll}
                className="inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest text-slate-600 hover:text-slate-900"
                data-testid="notifications-mark-all-read"
              >
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                <MessagesSquare className="w-6 h-6 mx-auto mb-2 text-zinc-700" />
                <div className="font-mono-num text-[11px] uppercase tracking-widest">No notifications yet</div>
                <div className="text-xs mt-1">You&apos;ll see replies to your posts here.</div>
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-200/60 hover:bg-slate-50 transition-colors block ${
                    n.read ? "opacity-70" : ""
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span
                        className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: brand }}
                        aria-label="unread"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 leading-snug flex items-start gap-1.5">
                        {n.type === "report" && <Flag className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />}
                        {n.type === "reaction" && <Flame className="w-3.5 h-3.5 text-[#0D9488] mt-0.5 shrink-0" />}
                        {n.type === "community_mention" && <AtSign className="w-3.5 h-3.5 text-[#0D9488] mt-0.5 shrink-0" />}
                        {n.type === "recap_reaction" && <span className="text-base leading-none mt-0.5 shrink-0" aria-hidden>💪</span>}
                        <div className="min-w-0 flex-1">
                          <span className="font-semibold">{n.actor_name}</span>{" "}
                          {n.type === "report" ? (
                            <span className="text-slate-600">reported a <span className="text-slate-900">{n.target_type}</span> — tap to review</span>
                          ) : n.type === "reaction" ? (
                            <span className="text-slate-600">reacted to your shot — tap to view</span>
                          ) : n.type === "community_mention" ? (
                            <><span className="text-slate-600">mentioned you in</span>{" "}<span className="font-semibold">&quot;{n.topic_title}&quot;</span></>
                          ) : n.type === "community_reply_to_comment" ? (
                            <><span className="text-slate-600">replied to your comment in</span>{" "}<span className="font-semibold">&quot;{n.topic_title}&quot;</span></>
                          ) : n.type === "recap_reaction" ? (
                            <><span className="text-slate-600">cheered your weekly recap</span>{" "}<span className="font-semibold">&quot;{n.topic_title}&quot;</span></>
                          ) : (
                            <><span className="text-slate-600">replied to</span>{" "}<span className="font-semibold">&quot;{n.topic_title}&quot;</span></>
                          )}
                        </div>
                      </div>
                      {n.body_preview && (
                        <div className="text-xs text-slate-500 mt-1 line-clamp-2 leading-snug">
                          {n.body_preview}
                        </div>
                      )}
                      <div className="text-[10px] font-mono-num uppercase tracking-widest text-slate-400 mt-1">
                        {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
